import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { useAuth } from "./AuthContext";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface TrialUser {
  firstName: string;
  lastName: string;
  role?: string;
  org?: string;
}

export interface Enrollment {
  programId: string;
  program: Record<string, unknown>;
  type: "trial" | "paid";
  enrolledOn: string;
  daysLeftInTrial?: number;
  overallProgress: number;
  sessionsCompleted: number;
  sessionsTotal: number;
  simulationsCompleted: number;
  simulationsTotal: number;
  completed?: boolean;
  completedOn?: string;
  earnedScore?: number;
  nextSession?: { title: string; duration: string; type?: "skill" | "sim" };
  streak?: number;
  lastActivity: string;
  trialLoop?: TrialLoopItem[];
}

export interface TrialLoopItem {
  type: "skill" | "sim" | "debrief";
  title: string;
  done?: boolean;
}

interface FreeTrialContextValue {
  isTrialActive: boolean;
  trialDaysRemaining: number;
  enrollments: Enrollment[];
  user: TrialUser | null;
  addEnrollment: (program: Record<string, unknown>) => void;
  setTrialUser: (user: TrialUser) => void;
}

// ── Context ───────────────────────────────────────────────────────────────────

const FreeTrialContext = createContext<FreeTrialContextValue | null>(null);

const TRIAL_DAYS = 10;

const storageKey = (userId: string) => `gn_trial_enrollments_v2_${userId}`;

// ── Provider ──────────────────────────────────────────────────────────────────

export const FreeTrialProvider = ({ children }: { children: React.ReactNode }) => {
  const { loggedInUser } = useAuth();
  const userId = (loggedInUser as { id?: string })?.id ?? "";

  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [user, setUser] = useState<TrialUser | null>(null);

  const ENROLLMENT_TTL_DAYS = 15;

  // Load this user's enrollments whenever the logged-in user changes
  useEffect(() => {
    if (!userId) {
      setEnrollments([]);
      return;
    }
    try {
      const raw = localStorage.getItem(storageKey(userId));
      const parsed: Enrollment[] = raw ? JSON.parse(raw) : [];
      const cutoff = Date.now() - ENROLLMENT_TTL_DAYS * 24 * 60 * 60 * 1000;
      setEnrollments(parsed.filter((e) => new Date(e.enrolledOn).getTime() > cutoff));
    } catch {
      setEnrollments([]);
    }
  }, [userId]);

  // Persist enrollments to the user-specific key
  useEffect(() => {
    if (!userId) return;
    try {
      localStorage.setItem(storageKey(userId), JSON.stringify(enrollments));
    } catch {
      // localStorage may be unavailable
    }
  }, [enrollments, userId]);

  const isTrialActive = enrollments.some((e) => e.type === "trial");
  const trialDaysRemaining = TRIAL_DAYS;

  const addEnrollment = useCallback((program: Record<string, unknown>) => {
    const programId = program.id as string;
    setEnrollments((prev) => {
      if (prev.some((e) => e.programId === programId)) return prev;

      type RawSession = { type: string; title: string; freeTrial?: boolean };
      const topics = (program.topics as Array<{ sessions: RawSession[] }>) ?? [];

      const freeTrialSessions = topics[0]?.sessions?.filter((s) => s.freeTrial) ?? [];
      const freeSkills = freeTrialSessions.filter((s) => s.type === "skill");
      const freeSims = freeTrialSessions.filter((s) => s.type === "sim");

      const sessionsTotal = freeSkills.length;
      const simsTotal = freeSims.length;

      const trialLoop: TrialLoopItem[] = freeTrialSessions.map((s) => ({
        type: s.type as "skill" | "sim",
        title: s.title,
        done: false,
      }));

      const nextSessionData = freeTrialSessions[0];

      const newEnrollment: Enrollment = {
        programId,
        program,
        type: "trial",
        enrolledOn: new Date().toISOString(),
        daysLeftInTrial: TRIAL_DAYS,
        overallProgress: 0,
        sessionsCompleted: 0,
        sessionsTotal,
        simulationsCompleted: 0,
        simulationsTotal: simsTotal,
        lastActivity: new Date().toISOString(),
        nextSession: nextSessionData
          ? { title: nextSessionData.title, duration: "12 min", type: nextSessionData.type as "skill" | "sim" }
          : undefined,
        streak: 0,
        trialLoop,
      };

      return [...prev, newEnrollment];
    });
  }, []);

  const setTrialUser = useCallback((newUser: TrialUser) => {
    setUser(newUser);
  }, []);

  return (
    <FreeTrialContext.Provider
      value={{ isTrialActive, trialDaysRemaining, enrollments, user, addEnrollment, setTrialUser }}
    >
      {children}
    </FreeTrialContext.Provider>
  );
};

// ── Hook ──────────────────────────────────────────────────────────────────────

export const useFreeTrialContext = (): FreeTrialContextValue => {
  const ctx = useContext(FreeTrialContext);
  if (!ctx) throw new Error("useFreeTrialContext must be used inside FreeTrialProvider");
  return ctx;
};
